import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { reviewService } from '../../../services/reviewService';
import { Button } from '../../../components/ui/Button';
import { Spinner } from '../../../components/ui/Spinner';
import { ReviewSection } from '../../../components/app/review/ReviewSection';
import { subDays, startOfMonth, endOfMonth, startOfDay, endOfDay } from 'date-fns';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

export const ReviewNetworking = () => {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const [period, setPeriod] = useState('30d'); // '30d', 'this_month', 'last_month', '90d'
  const [loading, setLoading] = useState(false);
  const [reviewData, setReviewData] = useState(null);
  const [error, setError] = useState(null);

  const getDatesForPeriod = (p) => {
    const now = new Date();
    switch (p) {
      case 'this_month':
        return { start: startOfMonth(now), end: endOfDay(now) };
      case 'last_month':
        const lastMonth = subDays(startOfMonth(now), 1);
        return { start: startOfMonth(lastMonth), end: endOfMonth(lastMonth) };
      case '90d':
        return { start: startOfDay(subDays(now, 90)), end: endOfDay(now) };
      case '30d':
      default:
        return { start: startOfDay(subDays(now, 30)), end: endOfDay(now) };
    }
  };

  useEffect(() => {
    if (user?.id) {
      loadReview();
    }
  }, [user, period]);

  const loadReview = async () => {
    setLoading(true);
    setError(null);
    setReviewData(null);
    
    try {
      const { start, end } = getDatesForPeriod(period);
      const existing = await reviewService.getLatestReview(user.id, start, end);
      
      if (existing) {
        setReviewData(existing);
      }
    } catch (err) {
      console.error(err);
      setError("Failed to load review history.");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const { start, end } = getDatesForPeriod(period);
      const objective = profile?.networking_objective || '';
      const newReview = await reviewService.generateReview(user.id, start, end, objective);
      setReviewData(newReview);
    } catch (err) {
      console.error(err);
      setError(err.message || "Failed to generate review. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getActionForNeedsAttention = (filter) => {
    if (filter === 'overdue') {
      return { label: 'Review Overdue Follow-ups', onClick: () => navigate('/app/reminders') };
    }
    if (filter === 'important') {
      return { label: 'View Important Connections', onClick: () => navigate('/app/connections?filter=important') };
    }
    return { label: 'Manage Connections', onClick: () => navigate('/app/connections') };
  };

  return (
    <div className="max-w-4xl pb-16">
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center text-sm font-medium text-slate-500">
          <button onClick={() => navigate('/app/dashboard')} className="hover:text-slate-900 flex items-center">
            <SafeIcon icon={FiIcons.FiArrowLeft} className="mr-1" /> Dashboard
          </button>
          <span className="mx-2">/</span>
          <span className="text-slate-900">Review</span>
        </div>
      </div>

      <header className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900">Review My Networking</h1>
          <p className="mt-2 text-slate-600">
            A concise, evidence-backed review of your networking performance.
          </p>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-3">
          <select 
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium focus:ring-2 focus:ring-slate-900 outline-none bg-white"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            disabled={loading}
          >
            <option value="30d">Last 30 days</option>
            <option value="this_month">This month</option>
            <option value="last_month">Previous month</option>
            <option value="90d">Last 90 days</option>
          </select>
          
          {reviewData && !reviewData.isFresh && (
            <Button variant="secondary" onClick={handleGenerate} disabled={loading}>
              <SafeIcon icon={FiIcons.FiRefreshCw} className="mr-2" /> Review Again
            </Button>
          )}
        </div>
      </header>

      {error && (
        <div className="bg-red-50 text-red-800 p-4 rounded-xl border border-red-200 mb-8 flex items-start">
          <SafeIcon icon={FiIcons.FiAlertCircle} className="text-xl mr-3 mt-0.5" />
          <p className="text-sm font-medium">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="py-24 flex flex-col items-center justify-center text-center">
          <Spinner size="lg" className="mb-6" />
          <h3 className="text-xl font-bold text-slate-900 mb-2">Analyzing your networking data</h3>
          <p className="text-slate-500 max-w-sm">Generating your professional briefing based on captured connections and outcomes.</p>
        </div>
      ) : !reviewData ? (
        <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-12 text-center">
          <div className="w-20 h-20 bg-blue-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <SafeIcon icon={FiIcons.FiTarget} className="text-4xl text-blue-500" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-3">Ready for your review?</h2>
          <p className="text-slate-600 max-w-md mx-auto mb-8 leading-relaxed">
            Get a 60-second summary of what generated the best results, which relationships need attention, and what to do next.
          </p>
          <Button size="lg" onClick={handleGenerate}>
            Generate Review
          </Button>
        </div>
      ) : (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
          {/* Overall */}
          <div className="bg-slate-900 rounded-2xl p-8 text-white shadow-md relative overflow-hidden">
            <div className="absolute right-0 top-0 w-64 h-64 bg-blue-500 rounded-full blur-3xl opacity-20 -mr-20 -mt-20 pointer-events-none"></div>
            <div className="relative z-10">
              <h2 className="text-xs font-bold uppercase tracking-widest text-blue-300 mb-3">
                {reviewData.review_content.overall.headline || 'Overall Performance'}
              </h2>
              <p className="text-xl md:text-2xl font-medium leading-relaxed">
                {reviewData.review_content.overall.summary}
              </p>
              <div className="mt-6 flex items-center text-xs text-slate-400 font-medium">
                <SafeIcon icon={FiIcons.FiClock} className="mr-1.5" />
                Generated on {new Date(reviewData.created_at).toLocaleDateString()}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6">
            {/* Recommendation - Brought to top for high visibility */}
            {reviewData.review_content.recommendation && (
              <ReviewSection 
                title={`Recommendation: ${reviewData.review_content.recommendation.title}`}
                summary={reviewData.review_content.recommendation.summary}
                confidence={reviewData.review_content.recommendation.confidence}
                evidenceKeys={reviewData.review_content.recommendation.evidence_keys}
                evidenceSnapshot={reviewData.evidence_snapshot}
                icon={FiIcons.FiZap}
                colorClass="bg-yellow-50 text-yellow-600 border border-yellow-100"
              />
            )}

            {/* Biggest Win */}
            {reviewData.review_content.biggest_win && (
              <ReviewSection 
                title={`Biggest Win: ${reviewData.review_content.biggest_win.title}`}
                summary={reviewData.review_content.biggest_win.summary}
                confidence={reviewData.review_content.biggest_win.confidence}
                evidenceKeys={reviewData.review_content.biggest_win.evidence_keys}
                evidenceSnapshot={reviewData.evidence_snapshot}
                icon={FiIcons.FiAward}
                colorClass="bg-emerald-50 text-emerald-600 border border-emerald-100"
              />
            )}

            {/* Needs Attention */}
            {reviewData.review_content.needs_attention && (
              <ReviewSection 
                title={`Needs Attention: ${reviewData.review_content.needs_attention.title}`}
                summary={reviewData.review_content.needs_attention.summary}
                evidenceKeys={reviewData.review_content.needs_attention.evidence_keys}
                evidenceSnapshot={reviewData.evidence_snapshot}
                icon={FiIcons.FiAlertCircle}
                colorClass="bg-red-50 text-red-600 border border-red-100"
                actionProps={getActionForNeedsAttention(reviewData.review_content.needs_attention.action_filter)}
              />
            )}

            {/* What Worked List */}
            {reviewData.review_content.what_worked?.length > 0 && reviewData.review_content.what_worked.map((item, idx) => (
              <ReviewSection 
                key={idx}
                title={`What Worked: ${item.title}`}
                summary={item.summary}
                confidence={item.confidence}
                evidenceKeys={item.evidence_keys}
                evidenceSnapshot={reviewData.evidence_snapshot}
                icon={FiIcons.FiTrendingUp}
                colorClass="bg-blue-50 text-blue-600 border border-blue-100"
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};